import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import type { SlackMessage, SlackReaction } from '@assembly/slack'
import { keys } from './keys'

export interface HistoryPage {
  /** Newest first. */
  messages: SlackMessage[]
  cursor?: string
}

export type HistoryData = InfiniteData<HistoryPage, string | undefined>

const isReply = (m: SlackMessage) => !!m.thread_ts && m.thread_ts !== m.ts

/** Whether a message belongs in the channel timeline (top-level or broadcast reply). */
export const inTimeline = (m: SlackMessage) => !isReply(m) || m.subtype === 'thread_broadcast'

function insertSorted(list: SlackMessage[], msg: SlackMessage, newestFirst: boolean): SlackMessage[] {
  const without = list.filter((m) => m.ts !== msg.ts)
  const existing = list.find((m) => m.ts === msg.ts)
  const merged = existing ? { ...existing, ...msg, pending: msg.pending ?? false } : msg
  without.push(merged)
  without.sort((a, b) => (newestFirst ? Number(b.ts) - Number(a.ts) : Number(a.ts) - Number(b.ts)))
  return without
}

function mapHistory(qc: QueryClient, account: string, channel: string, fn: (pages: HistoryPage[]) => HistoryPage[]) {
  qc.setQueryData<HistoryData>(keys.history(account, channel), (data) => (data ? { ...data, pages: fn(data.pages) } : data))
}

function mapReplies(qc: QueryClient, account: string, channel: string, threadTs: string, fn: (list: SlackMessage[]) => SlackMessage[]) {
  qc.setQueryData<SlackMessage[]>(keys.replies(account, channel, threadTs), (list) => (list ? fn(list) : list))
}

/** Applies `fn` to a message wherever it is cached (timeline and any thread). */
export function updateMessage(qc: QueryClient, account: string, channel: string, ts: string, fn: (m: SlackMessage) => SlackMessage) {
  mapHistory(qc, account, channel, (pages) => pages.map((p) => ({ ...p, messages: p.messages.map((m) => (m.ts === ts ? fn(m) : m)) })))
  for (const [key] of qc.getQueriesData<SlackMessage[]>({ queryKey: ['replies', account, channel] })) {
    qc.setQueryData<SlackMessage[]>(key, (list) => list?.map((m) => (m.ts === ts ? fn(m) : m)))
  }
}

/** Inserts or merges a message into the timeline and/or its thread. */
export function upsertMessage(qc: QueryClient, account: string, channel: string, msg: SlackMessage) {
  if (inTimeline(msg)) {
    mapHistory(qc, account, channel, (pages) => {
      if (!pages.length) return pages
      const found = pages.findIndex((p) => p.messages.some((m) => m.ts === msg.ts))
      const idx = found === -1 ? 0 : found
      return pages.map((p, i) => (i === idx ? { ...p, messages: insertSorted(p.messages, msg, true) } : p))
    })
  }
  if (msg.thread_ts) {
    const threadTs = msg.thread_ts
    const seen = qc.getQueryData<SlackMessage[]>(keys.replies(account, channel, threadTs))?.some((m) => m.ts === msg.ts)
    mapReplies(qc, account, channel, threadTs, (list) => insertSorted(list, msg, false))
    // Bump the parent's reply metadata the first time a confirmed reply is seen.
    if (isReply(msg) && !msg.pending && !seen) {
      updateMessage(qc, account, channel, threadTs, (parent) => ({
        ...parent,
        reply_count: (parent.reply_count ?? 0) + 1,
        reply_users: [...new Set([...(parent.reply_users ?? []), ...(msg.user ? [msg.user] : [])])],
        latest_reply: msg.ts,
      }))
    }
  }
}

export function removeMessage(qc: QueryClient, account: string, channel: string, ts: string) {
  mapHistory(qc, account, channel, (pages) => pages.map((p) => ({ ...p, messages: p.messages.filter((m) => m.ts !== ts) })))
  for (const [key] of qc.getQueriesData<SlackMessage[]>({ queryKey: ['replies', account, channel] })) {
    qc.setQueryData<SlackMessage[]>(key, (list) => list?.filter((m) => m.ts !== ts))
  }
}

/** Replaces the newest window of the timeline with a fresh fetch (used when polling). */
export function reconcileLatest(qc: QueryClient, account: string, channel: string, fresh: SlackMessage[]) {
  if (!fresh.length) return
  const oldest = Number(fresh[fresh.length - 1].ts)
  mapHistory(qc, account, channel, (pages) => {
    if (!pages.length) return pages
    const pending = pages[0].messages.filter((m) => m.pending)
    const rest = pages[0].messages.filter((m) => !m.pending && Number(m.ts) < oldest)
    const merged = [...pending, ...fresh, ...rest].sort((a, b) => Number(b.ts) - Number(a.ts))
    return [{ ...pages[0], messages: merged }, ...pages.slice(1)]
  })
}

export function applyReaction(m: SlackMessage, name: string, user: string, added: boolean): SlackMessage {
  const reactions: SlackReaction[] = [...(m.reactions ?? [])]
  const i = reactions.findIndex((r) => r.name === name)
  if (added) {
    if (i === -1) reactions.push({ name, count: 1, users: [user] })
    else if (!reactions[i].users.includes(user)) reactions[i] = { ...reactions[i], count: reactions[i].count + 1, users: [...reactions[i].users, user] }
  } else if (i !== -1 && reactions[i].users.includes(user)) {
    const r = reactions[i]
    if (r.count <= 1) reactions.splice(i, 1)
    else reactions[i] = { ...r, count: r.count - 1, users: r.users.filter((u) => u !== user) }
  }
  return { ...m, reactions }
}

export function findMessage(qc: QueryClient, account: string, channel: string, ts: string): SlackMessage | undefined {
  const history = qc.getQueryData<HistoryData>(keys.history(account, channel))
  for (const p of history?.pages ?? []) {
    const m = p.messages.find((x) => x.ts === ts)
    if (m) return m
  }
  for (const [, list] of qc.getQueriesData<SlackMessage[]>({ queryKey: ['replies', account, channel] })) {
    const m = list?.find((x) => x.ts === ts)
    if (m) return m
  }
  return undefined
}
